import { BrowserFeatureError, MatchTransportError } from "./errors";

const DEFAULT_NONCE_BYTES = 32;

function webCrypto(): Crypto {
  const candidate = globalThis.crypto;
  if (!candidate?.subtle || typeof candidate.getRandomValues !== "function") {
    throw new BrowserFeatureError(
      "WebCrypto",
      "secure contexts (HTTPS or localhost) are required",
    );
  }
  return candidate;
}

function utf8(value: string): Uint8Array {
  if (typeof TextEncoder === "undefined") {
    throw new BrowserFeatureError("TextEncoding");
  }
  return new TextEncoder().encode(value);
}

export function bytesToHex(bytes: ArrayBuffer | ArrayBufferView): string {
  const view =
    bytes instanceof ArrayBuffer
      ? new Uint8Array(bytes)
      : new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let result = "";
  for (const byte of view) result += byte.toString(16).padStart(2, "0");
  return result;
}

export function hexToBytes(hex: string): Uint8Array {
  if (!/^(?:[0-9a-fA-F]{2})+$/.test(hex)) {
    throw new MatchTransportError("seed", "Expected an even-length hexadecimal value");
  }
  const result = new Uint8Array(hex.length / 2);
  for (let index = 0; index < result.length; index += 1) {
    result[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  }
  return result;
}

export async function sha256Hex(data: string | BufferSource): Promise<string> {
  const source = typeof data === "string" ? utf8(data) : data;
  const sourceView =
    source instanceof ArrayBuffer
      ? new Uint8Array(source)
      : new Uint8Array(source.buffer, source.byteOffset, source.byteLength);
  // Copy into an ArrayBuffer-backed view; TS correctly refuses a possible
  // SharedArrayBuffer for Web Crypto even though most browser views use one.
  const bytes = new Uint8Array(sourceView.byteLength);
  bytes.set(sourceView);
  return bytesToHex(await webCrypto().subtle.digest("SHA-256", bytes.buffer));
}

/** Returns a cryptographically random nonce encoded as lowercase hex. */
export function generateNonce(byteLength = DEFAULT_NONCE_BYTES): string {
  if (!Number.isSafeInteger(byteLength) || byteLength < 16 || byteLength > 1024) {
    throw new RangeError("Nonce byteLength must be an integer between 16 and 1024");
  }
  const bytes = new Uint8Array(byteLength);
  webCrypto().getRandomValues(bytes);
  return bytesToHex(bytes);
}

function commitMaterial(nonce: string, context: string): string {
  return `BWWK-SEED-COMMIT\u0000${context}\u0000${nonce}`;
}

/** Commit to a nonce before either peer reveals it. */
export function commitNonce(nonce: string, context: string): Promise<string> {
  if (!nonce || !context) {
    return Promise.reject(new MatchTransportError("seed", "Nonce and context are required"));
  }
  return sha256Hex(commitMaterial(nonce, context));
}

export async function verifyNonceCommit(
  nonce: string,
  expectedCommit: string,
  context: string,
): Promise<boolean> {
  const actual = await commitNonce(nonce, context);
  return timingSafeStringEqual(actual, expectedCommit.toLowerCase());
}

/** Constant-work comparison for equal-size ASCII digests. */
export function timingSafeStringEqual(left: string, right: string): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= left.charCodeAt(index) ^ right.charCodeAt(index);
  }
  return difference === 0;
}

export interface SeedReveal {
  readonly senderId: string;
  readonly nonce: string;
}

/**
 * Both clients receive the same seed regardless of reveal arrival order.
 * The full SHA-256 hex is returned so the rules core can deterministically
 * expand it without relying on a platform random API.
 */
export async function deriveSharedSeed(
  matchId: string,
  reveals: readonly SeedReveal[],
  generatorVersion = "bwwk-cue-v1",
): Promise<string> {
  if (!matchId || reveals.length < 2) {
    throw new MatchTransportError("seed", "A match id and both peer reveals are required");
  }
  const seen = new Set<string>();
  const canonical = [...reveals]
    .map(({ senderId, nonce }) => {
      if (!senderId || !nonce || seen.has(senderId)) {
        throw new MatchTransportError("seed", "Seed reveals must have unique sender ids");
      }
      seen.add(senderId);
      return `${senderId.length}:${senderId}:${nonce.length}:${nonce}`;
    })
    .sort()
    .join("|");
  return sha256Hex(
    `BWWK-SHARED-SEED\u0000${generatorVersion}\u0000${matchId}\u0000${canonical}`,
  );
}

export interface SeedCommitRevealState {
  readonly nonce: string;
  readonly commit: string;
}

export async function createSeedCommit(
  matchId: string,
  senderId: string,
): Promise<SeedCommitRevealState> {
  const nonce = generateNonce();
  return {
    nonce,
    commit: await commitNonce(nonce, `${matchId}:${senderId}`),
  };
}
